import { LoadingButton } from "@mui/lab";
import {
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  getActivitiesWithEmailTemplates,
  getEmailTemplates,
} from "backend/api/emailing";
import { useError } from "hooks/useError";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { BasicActivity } from "types/activity.type";
import { EmailTemplate } from "types/emailTemplate.type";
import { HEADER_OPTIONS } from "./EmailTemplateAssignmentTable.constants";
import "./EmailTemplateAssignmentTable.css";
import {
  generateFiledName,
  generateFilteredTemplates,
  getEmailTemplatesFromFormData,
  setActivityEmailTemplatesFormValue,
} from "./EmailTemplateAssignmentTable.helpers";
import {
  EmailTemplateAssignmentForm,
  EmailTemplateAssignmentTableProps,
} from "./EmailTemplateAssignmentTable.type";
import { EmailTemplateAssignmentTableCell } from "./EmailTemplateAssignmentTableCell/EmailTemplateAssignmentTableCell";

export function EmailTemplateAssignmentTable({
  isDisabled,
  isSubmitting,
  onSubmit,
}: EmailTemplateAssignmentTableProps) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [activities, setActivities] = useState<BasicActivity[]>([]);

  const { t } = useTranslation("emailing");
  const { onError } = useError(t, "errors.fetchError");

  const { control, handleSubmit, setValue } =
    useForm<EmailTemplateAssignmentForm>();

  const { isLoading: isLoadingTemplates } = useQuery({
    queryKey: ["emailTemplates"],
    queryFn: () => getEmailTemplates(),
    onSuccess: (data) => setTemplates(data.data),
    onError,
  });

  const { isLoading: isLoadingActivities } = useQuery({
    queryKey: ["activitiesWithEmailTemplates"],
    queryFn: () => getActivitiesWithEmailTemplates(),
    onError,
    onSuccess: (data) => {
      if (data?.data) {
        const { activities: fetchedActivities, activityEmailTemplates } =
          data.data;
        setActivityEmailTemplatesFormValue(
          fetchedActivities,
          activityEmailTemplates,
          setValue,
        );
        setActivities(fetchedActivities);
      }
    },
  });

  const isLoading = isLoadingTemplates || isLoadingActivities;

  const FilteredTemplates = useMemo(
    () => generateFilteredTemplates(templates),
    [templates],
  );

  const handleFormSubmit = (data: EmailTemplateAssignmentForm) => {
    if (onSubmit) {
      onSubmit(getEmailTemplatesFromFormData(data));
    }
  };

  return (
    <Stack
      component={"form"}
      spacing={4}
      onSubmit={handleSubmit(handleFormSubmit)}
    >
      <TableContainer className="table-container">
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className="table-head-cell">
                {t("assignEmail.activity")}
              </TableCell>
              {HEADER_OPTIONS.map((option) => (
                <TableCell
                  className="table-head-cell"
                  key={option.type}
                  align="center"
                >
                  {t(option.label)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={HEADER_OPTIONS.length + 1}>
                  <Stack
                    className="stack-container"
                    alignItems={"center"}
                    justifyContent={"center"}
                  >
                    <CircularProgress size={24} />
                  </Stack>
                </TableCell>
              </TableRow>
            )}
            {activities.map((activity) => (
              <TableRow key={activity.id}>
                <TableCell className="activity-name-cell">
                  {activity.title}
                </TableCell>
                {HEADER_OPTIONS.map((option) => {
                  return (
                    <Controller
                      key={`${activity.id}-${option.type}`}
                      name={generateFiledName(activity.id, option.type)}
                      control={control}
                      render={({ field }) => (
                        <EmailTemplateAssignmentTableCell
                          value={field.value}
                          activityId={activity.id}
                          templates={FilteredTemplates[option.type]}
                          type={option.type}
                          isDisabled={isDisabled}
                          onChange={(value) => {
                            field.onChange(value);
                          }}
                          onRemove={() =>
                            field.onChange({
                              activityId: activity.id,
                              triggerEvent: option.type,
                              templateId: null,
                            })
                          }
                        />
                      )}
                    />
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack alignItems={"center"}>
        <LoadingButton
          loading={isSubmitting}
          disabled={isDisabled}
          type="submit"
          variant="contained"
        >
          {t("assignEmail.saveTemplate")}
        </LoadingButton>
      </Stack>
    </Stack>
  );
}
